import { tr } from '@/contexts/LanguageContext';
/**
 * Backend API client. Centralizes:
 *   - Base URL (REACT_APP_BACKEND_URL + /api)
 *   - Bearer token handling (persisted in localStorage as `lineage_token`)
 *   - JSON request/response
 *   - User shape normalization (snake_case → camelCase for legacy frontend)
 *
 * Usage:
 *   import { api } from '@/lib/api';
 *   const me = await api.get('/auth/me');
 *   const { user, access_token } = await api.post('/auth/login', { email, password });
 */
const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = 'lineage_token';
// Iter 111 — language preference is read from the same localStorage key
// LanguageContext writes to. We don't import the context here to avoid a
// circular dep (the context already runs before the first request).
const LANG_KEY  = 'lineage_lang';
const ACCEPT_LANGUAGE_FALLBACK = 'en';
const SUPPORTED_LANGS = ['en', 'es', 'pt', 'fr'];

const _norm = (raw) => {
  const code = String(raw || '').toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGS.includes(code) ? code : null;
};

// Misma regla que LanguageContext: elección guardada → idioma del dispositivo → inglés.
const getCurrentLang = () => {
  try { const stored = _norm(localStorage.getItem(LANG_KEY)); if (stored) return stored; } catch { /* private mode */ }
  try {
    const list = (navigator.languages && navigator.languages.length) ? navigator.languages : [navigator.language];
    for (const c of list) { const n = _norm(c); if (n) return n; }
  } catch { /* noop */ }
  return ACCEPT_LANGUAGE_FALLBACK;
};

export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
};

export const setToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
};

const buildHeaders = (extra = {}) => {
  const h = { 'Content-Type': 'application/json', ...extra };
  const t = getToken();
  if (t) h['Authorization'] = `Bearer ${t}`;
  // Iter 111 — backend reads Accept-Language for translating critical
  // error messages (auth / register / invite / upload 503). Doesn't
  // affect business logic — it's a presentation hint only.
  if (!h['Accept-Language']) h['Accept-Language'] = getCurrentLang();
  // Payment Classifier — la plataforma decide qué métodos de pago se permiten.
  if (!h['X-LBW-Platform']) h['X-LBW-Platform'] = 'web';
  // Vista fiel founder (jun-2026) — el rol de vista viaja al backend; solo
  // lo respeta si la sesión real es super_admin (gates de elegibilidad del
  // Marketplace). Inofensivo para cualquier otro usuario.
  try {
    const vr = localStorage.getItem('lineage_view_role');
    if (vr && !h['X-View-Role']) h['X-View-Role'] = vr;
  } catch { /* private mode */ }
  // Demos founder (jun-2026) — 'Entrar como demo': el backend SOLO lo
  // respeta si la sesión real es super_admin/founder y el objetivo es una
  // cuenta is_demo. Inofensivo para cualquier otro usuario.
  try {
    const du = localStorage.getItem('lineage_demo_user');
    if (du && !h['X-Demo-User']) h['X-Demo-User'] = du;
  } catch { /* private mode */ }
  return h;
};

// Bug «inglés en blanco» (oct-2026): cuando una llamada falla sin JSON útil, el fallo se anota en el
// servidor (POST /api/client-log) para poder leerlo en los logs aunque la petición original nunca llegara.
// Máximo 8 avisos por carga, uno por ruta+estado. Sin PII: ruta sin query, estado, mensaje corto, idioma.
const _reported = new Set();
export const reportApiFailure = ({ method, path, status, message, rid }) => {
  try {
    if (path === '/client-log' || path === '/csp-report') return;
    const key = `${method} ${String(path).split('?')[0]} ${status}`;
    if (_reported.has(key) || _reported.size >= 8) return;
    _reported.add(key);
    const standalone = Boolean(window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone);
    const body = JSON.stringify({
      method, path: String(path).split('?')[0].slice(0, 160), status, message: String(message || '').slice(0, 240),
      lang: getCurrentLang(), rid: rid || null, screen: window.location?.pathname?.slice(0, 80) || '',
      standalone, online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    });
    fetch(`${BASE_URL}/client-log`, {
      method: 'POST', body, keepalive: true, credentials: 'omit',
      headers: { 'Content-Type': 'application/json', 'Accept-Language': getCurrentLang() },
    }).catch(() => {});
  } catch { /* nunca romper la UI por un aviso */ }
};

/** Texto corto y legible del fallo (para tarjetas de error con «Reintentar»). */
export const describeApiError = (err) => {
  if (!err) return '';
  if (err.networkError || err.status === 0) return tr("Sin conexión con el servidor. Revisa tu internet.");
  const d = err?.payload?.detail;
  if (typeof d === 'string' && d.trim() && d.length <= 160) return d;
  if (err.status) return tr("Algo salió mal. Inténtalo de nuevo.");
  return err.message || tr("Algo salió mal.");
};

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Texto claro (sin código) cuando el servidor no mandó un `detail` legible. */
export const friendlyStatus = (status) => {
  if (status === 401) return tr("Tu sesión expiró. Inicia sesión de nuevo.");
  if (status === 403) return tr("No tienes permiso para hacer esto.");
  if (status === 404) return tr("No encontramos lo que buscas.");
  if (status === 429) return tr("Demasiados intentos. Espera un momento e inténtalo de nuevo.");
  if (status >= 500) return tr("El servidor no respondió. Inténtalo de nuevo en unos segundos.");
  return tr("Algo salió mal. Inténtalo de nuevo.");
};

// 2FA (Paso 2): una acción sensible responde 403 STEP_UP_REQUIRED → se abre el diálogo global,
// se espera el código y se reintenta la petición UNA vez. Admin sin 2FA → 403 MFA_SETUP_REQUIRED → pantalla de activación.
const waitForStepUp = () => new Promise((resolve) => {
  const done = (e) => { window.removeEventListener('ziyup:step-up-done', done); resolve(Boolean(e?.detail?.ok)); };
  window.addEventListener('ziyup:step-up-done', done);
  window.dispatchEvent(new CustomEvent('ziyup:step-up-required'));
});

// Paso 2 (oct-2026) — sesión persistente: ante un 401 el frontend intenta PRIMERO /auth/refresh (una sola vez, en
// «single-flight»: muchas peticiones simultáneas comparten el mismo refresh) y reintenta; solo si el refresh falla
// se cierra la sesión y se manda al login.
let _refreshInFlight = null;
const refreshSession = () => {
  if (!_refreshInFlight) {
    _refreshInFlight = fetch(`${BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include', headers: buildHeaders() })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { setTimeout(() => { _refreshInFlight = null; }, 0); });
  }
  return _refreshInFlight;
};

const handleSessionExpired = (path) => {
  try { setToken(null); } catch { /* ignore */ }
  if (typeof window === 'undefined') return;
  // Rutas públicas de cuenta (recuperar contraseña, confirmar correo, login, consentimientos,
  // legales): un 401 de fondo (sesión caducada en caché) NUNCA debe sacar al usuario de ahí.
  const PUBLIC_NO_REDIRECT = ['/auth', '/reset-password', '/confirmar-email', '/consent', '/legal', '/verificar-premio', '/activar-cuenta', '/r', '/cita', '/pagar', '/resena'];
  const pathname = window.location?.pathname || '';
  const onPublicScreen = PUBLIC_NO_REDIRECT.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const alreadyRedirected = window.__lineage_session_redirect_armed__ === true;
  if (!onPublicScreen && !alreadyRedirected) {
    window.__lineage_session_redirect_armed__ = true;
    setTimeout(() => {
      if (!PUBLIC_NO_REDIRECT.some((p) => window.location.pathname === p || window.location.pathname.startsWith(`${p}/`))) {
        window.location.assign('/auth?reason=session_expired');
      }
    }, 50);
  }
};

// Rutas que legítimamente devuelven 401 y NO deben disparar refresh (login, refresh, registro, 2FA, contraseña).
const NO_REFRESH = ['/auth/login', '/auth/refresh', '/auth/register', '/auth/mfa/', '/auth/logout', '/auth/forgot-password', '/auth/reset-password', '/auth/activate'];

const request = async (method, path, body = null, opts = {}) => {
  try {
    return await requestOnce(method, path, body, opts);
  } catch (err) {
    const noRefresh = NO_REFRESH.some((p) => path.startsWith(p));
    if (err?.status === 401 && !noRefresh && !opts.__refreshed) {
      const ok = await refreshSession();
      if (ok) return request(method, path, body, { ...opts, __refreshed: true });
      if (!path.startsWith('/auth/')) handleSessionExpired(path);
      throw err;
    }
    if (err?.status === 401 && !noRefresh && opts.__refreshed) {
      if (!path.startsWith('/auth/')) handleSessionExpired(path);
      throw err;
    }
    if (err?.status === 403 && err?.code === 'STEP_UP_REQUIRED' && !opts.__retried && typeof window !== 'undefined') {
      const ok = await waitForStepUp();
      if (ok) return requestOnce(method, path, body, { ...opts, __retried: true });
      const cancelled = new Error(tr('Acción cancelada.'));
      cancelled.status = 403; cancelled.code = 'STEP_UP_CANCELLED'; cancelled.payload = err.payload;
      throw cancelled;
    }
    if (err?.status === 403 && err?.code === 'MFA_SETUP_REQUIRED' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ziyup:mfa-setup-required'));
    }
    throw err;
  }
};

const requestOnce = async (method, path, body = null, opts = {}) => {
  let res;
  try {
    const doFetch = () => fetch(`${BASE_URL}${path}`, {
      method,
      headers: buildHeaders(opts.headers),
      body: body ? JSON.stringify(body) : null,
      credentials: 'include',
    });
    try {
      res = await doFetch();
    } catch (first) {
      // Microcorte de red / arranque de pod (13-oct-2026, «Load failed» status=0): las lecturas se reintentan una vez.
      if (method !== 'GET') throw first;
      await _sleep(700);
      res = await doFetch();
    }
  } catch (netErr) {
    // Network-level failure (CORS, DNS, offline, blocked). Surface a clear
    // message instead of a cryptic 'Failed to fetch'.
    // eslint-disable-next-line no-console
    console.error(`[api] ${method} ${path} → network error`, netErr);
    const err = new Error(tr("Sin conexión con el servidor ({0})", netErr?.message || 'network error'));
    err.status = 0;
    err.networkError = true;
    err.method = method;
    err.path = path;
    reportApiFailure({ method, path, status: 0, message: netErr?.message || 'network error' });
    throw err;
  }
  let payload = null;
  try { payload = await res.json(); } catch { /* not JSON */ }
  if (!res.ok) {
    // Build the most informative message possible. Even when the backend
    // returns an empty body (proxy 502 etc.) we surface the status code so
    // we never show a context-free "Request failed".
    // Arreglo founder 14-oct-2026 — el usuario nunca ve códigos técnicos ("(HTTP 400)"): solo el texto claro.
    const detail = payload?.detail || payload?.message || friendlyStatus(res.status);
    const err = new Error(typeof detail === 'string' ? detail : (detail?.message || friendlyStatus(res.status)));
    err.status = res.status;
    err.payload = payload;
    err.method = method;
    err.path = path;
    err.requestId = res.headers.get('x-request-id') || null;
    err.code = (detail && typeof detail === 'object' && detail.code) || null;
    // Cliente sin contraseña tocando algo bloqueado (acción, no lectura): «Para usar esto, crea tu contraseña».
    if (res.status === 403 && err.code === 'PASSWORD_REQUIRED' && method !== 'GET' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('lineage:password-required', { detail: { path } }));
    }
    // Fallo sin JSON útil (HTML del borde, 5xx, cuerpo vacío) → queda en los logs del servidor.
    if (res.status >= 500 || !payload || (!payload.detail && !payload.message)) {
      reportApiFailure({ method, path, status: res.status, message: detail, rid: err.requestId });
    }
    // Console log so devs always see what failed without opening Network tab.
    // eslint-disable-next-line no-console
    console.error(`[api] ${method} ${path} → ${res.status}`, payload || res.statusText);
    // Auto-logout on stale JWT — UX safety net so users don't get stuck in
    // a zombie state when the backend invalidates their token (restart,
    // password change, etc.).
    //
    // Iter 138 — redirect-loop hardening. Three guards prevent the
    // login screen from cycling indefinitely when the httpOnly cookie
    // is rejected by the browser (e.g. SameSite block inside an
    // iframed preview):
    //   (1) Skip auto-redirect for any /auth/* call — those are
    //       inherently allowed to 401 (login probe, refresh, /me).
    //   (2) Skip auto-redirect when already sitting on /auth.
    //   (3) Throttle: a single redirect per session via a window-level
    //       sentinel so even pathological code paths can't ping-pong.
    throw err;
  }
  return payload;
};

export const api = {
  get:    (path, opts) => request('GET', path, null, opts),
  post:   (path, body, opts) => request('POST', path, body, opts),
  put:    (path, body, opts) => request('PUT', path, body, opts),
  patch:  (path, body, opts) => request('PATCH', path, body, opts),
  delete: (path, opts) => request('DELETE', path, null, opts),
  // Iter 147 — multipart file upload helper. Same auth + error contract
  // as the JSON methods but does NOT set Content-Type (the browser needs
  // to attach the multipart boundary itself).
  postForm: async (path, formData, opts = {}) => {
    const headers = {};
    const t = getToken();
    if (t) headers['Authorization'] = `Bearer ${t}`;
    if (!headers['Accept-Language']) headers['Accept-Language'] = getCurrentLang();
    Object.assign(headers, opts.headers || {});
    let res;
    try {
      res = await fetch(`${BASE_URL}${path}`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });
    } catch (netErr) {
      const err = new Error(tr("Sin conexión con el servidor ({0})", netErr?.message || 'network error'));
      err.status = 0;
      err.networkError = true;
      throw err;
    }
    let payload = null;
    try { payload = await res.json(); } catch { /* not JSON */ }
    if (!res.ok) {
      const detail = payload?.detail || payload?.message || friendlyStatus(res.status);
      const err = new Error(typeof detail === 'string' ? detail : (detail?.message || friendlyStatus(res.status)));
      err.status = res.status;
      err.payload = payload;
      throw err;
    }
    return payload;
  },
};

/**
 * Normalize a backend user record to the camelCase shape the frontend expects.
 * Preserves existing snake_case keys for any code that already reads them.
 */
export const normalizeUser = (raw) => {
  if (!raw) return null;
  return {
    ...raw,
    rankingDisplay: raw.rankingDisplay ?? raw.ranking_display ?? 'world',
    extraPermissions: raw.extraPermissions ?? raw.extra_permissions ?? [],
    permissions: raw.permissions || [],
    // Some legacy callers read these:
    followers: raw.followers ?? 0,
    following: raw.following ?? 0,
    wins: raw.wins ?? 0,
    rating: raw.rating ?? 0,
  };
};

export const authApi = {
  async login(email, password) {
    // Iter 134 — token-less response. The httpOnly cookie set by the
    // backend is the single carrier of the JWT; we no longer touch
    // localStorage at all. Defensive setToken(null) wipes any stale
    // token a previous build may have left behind.
    const data = await api.post('/auth/login', { email, password });
    setToken(null);
    if (data.mfa_required) return data;
    return { ...data, user: normalizeUser(data.user) };
  },
  async mfaVerify(code, rememberDevice = false) {
    const data = await api.post('/auth/mfa/verify', { code, remember_device: Boolean(rememberDevice) });
    return { ...data, user: normalizeUser(data.user) };
  },
  async register(payload) {
    const data = await api.post('/auth/register', payload);
    setToken(null);
    return { ...data, user: normalizeUser(data.user) };
  },
  async logout() {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setToken(null);
  },
  async logoutAll() {
    try { await api.post('/auth/logout-all'); } catch { /* ignore */ }
    setToken(null);
  },
  sessions: () => api.get('/auth/sessions'),
  forgetDevice: (id) => api.delete(`/auth/sessions/devices/${id}`),
  closeSession: (id) => api.delete(`/auth/sessions/${id}`),
  async me() {
    const data = await api.get('/auth/me');
    return normalizeUser(data);
  },
};

export default api;
